const VK_APP_ID = process.env.REACT_APP_VK_APP_ID || "54546090";
const VK_REDIRECT_URI =
  process.env.REACT_APP_VK_REDIRECT_URI || "https://app.clean-nature.org/vk/mobile/callback";
const VK_AUTHORIZE_URL = "https://id.vk.ru/authorize";
const VK_SCOPE = "email";
const PENDING_KEY = "vkAuthPending";
const PENDING_TTL_MS = 15 * 60 * 1000;

// Marker prepended to the OAuth `state` so the shared /vk/mobile/callback can
// tell web flows (serve the SPA) from the native RN flow (bounce to the app's
// custom scheme). MUST be pure base64url (here: alphanumeric) — VK ID does NOT
// round-trip a "." in the state param, but it does preserve base64url (the RN
// flow relies on that). Keep this in sync with cleancy_server/server.js.
export const WEB_STATE_PREFIX = "cleancyvkweb";

export type VkAuthMode = "login" | "link" | "migration";

export interface VkMigrationPending {
  migrationToken: string;
  phone: string;
  phoneVerificationToken: string;
}

export interface VkPending {
  codeVerifier: string;
  state: string;
  mode: VkAuthMode;
  bridgeNonce?: string;
  migration?: VkMigrationPending;
  createdAt: number;
}

export const VK_REDIRECT_URI_VALUE = VK_REDIRECT_URI;

export function isVkConfigured(): boolean {
  return /^\d+$/.test(VK_APP_ID);
}

function base64url(bytes: Uint8Array): string {
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

export function generateCodeVerifier(): string {
  return base64url(crypto.getRandomValues(new Uint8Array(64)));
}

export async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(codeVerifier));
  return base64url(new Uint8Array(digest));
}

export function webState(): string {
  return WEB_STATE_PREFIX + base64url(crypto.getRandomValues(new Uint8Array(32)));
}

export function buildVkAuthorizeUrl(p: { state: string; codeChallenge: string }): string {
  const q = new URLSearchParams({
    client_id: VK_APP_ID,
    response_type: "code",
    redirect_uri: VK_REDIRECT_URI,
    code_challenge_method: "s256",
    code_challenge: p.codeChallenge,
    state: p.state,
    scope: VK_SCOPE,
  });
  return `${VK_AUTHORIZE_URL}?${q.toString()}`;
}

export function readVkPending(): VkPending | null {
  const raw = sessionStorage.getItem(PENDING_KEY);
  if (!raw) return null;
  try {
    const pending = JSON.parse(raw) as VkPending;
    if (
      !pending.codeVerifier ||
      !pending.state ||
      !pending.createdAt ||
      Date.now() - pending.createdAt > PENDING_TTL_MS
    ) {
      sessionStorage.removeItem(PENDING_KEY);
      return null;
    }
    sessionStorage.removeItem(PENDING_KEY);
    return pending;
  } catch {
    sessionStorage.removeItem(PENDING_KEY);
    return null;
  }
}

export async function startVkAuth(opts: {
  mode: VkAuthMode;
  bridgeNonce?: string;
  migration?: VkMigrationPending;
}): Promise<void> {
  const codeVerifier = generateCodeVerifier();
  const state = webState();
  const pending: VkPending = {
    codeVerifier,
    state,
    mode: opts.mode,
    bridgeNonce: opts.bridgeNonce,
    migration: opts.migration,
    createdAt: Date.now(),
  };
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  window.location.assign(buildVkAuthorizeUrl({ state, codeChallenge }));
}
